import React, { useState, useMemo } from 'react'
import { DataGrid } from '@mui/x-data-grid'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import InputAdornment from '@mui/material/InputAdornment'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import SearchIcon from '@mui/icons-material/Search'
import FilterListIcon from '@mui/icons-material/FilterList'

const ROLE_LABELS = {
  siapAdmin: 'Super Admin',
  siapDeveloper: 'Developer SIAP',
  agencyAdmin: 'Admin Agenzia'
}

const ROLE_COLORS = {
  siapAdmin: 'primary',
  siapDeveloper: 'info',
  agencyAdmin: 'secondary'
}

export const UsersTable = ({
  users,
  agencies,
  isAdmin,
  canEdit,
  onEdit,
  onDelete
}) => {
  const [searchFilter, setSearchFilter] = useState('')
  const [roleFilter, setRoleFilter] = useState('')
  const [agencyFilter, setAgencyFilter] = useState('')

  const agencyMap = useMemo(() => {
    const map = {}
    agencies?.forEach((a) => { map[a._id] = a.name || a._id })
    return map
  }, [agencies])

  const filteredUsers = useMemo(() => {
    let result = users || []

    if (searchFilter) {
      const q = searchFilter.toLowerCase()
      result = result.filter((u) =>
        (u.name || '').toLowerCase().includes(q) ||
        (u.lastName || '').toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q)
      )
    }

    if (roleFilter) {
      result = result.filter((u) => u.role === roleFilter)
    }

    if (agencyFilter) {
      result = result.filter((u) =>
        u.allAgencies?.some((a) => a._id === agencyFilter) ||
        u.agency?._id === agencyFilter
      )
    }

    return result
  }, [users, searchFilter, roleFilter, agencyFilter])

  const hasActiveFilters = searchFilter || roleFilter || agencyFilter

  const handleClearFilters = () => {
    setSearchFilter('')
    setRoleFilter('')
    setAgencyFilter('')
  }

  const columns = [
    {
      field: 'name',
      headerName: 'Nome',
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Typography variant='body2' fontWeight={500} noWrap>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'lastName',
      headerName: 'Cognome',
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Typography variant='body2' fontWeight={500} noWrap>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1.5,
      minWidth: 180,
      renderCell: (params) => (
        <Typography variant='body2' fontWeight={500} noWrap>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'role',
      headerName: 'Ruolo',
      width: 160,
      renderCell: (params) => (
        <Chip
          label={ROLE_LABELS[params.value] || params.value}
          color={ROLE_COLORS[params.value] || 'default'}
          size='small'
          variant='outlined'
          sx={{ fontWeight: 600, fontSize: 12 }}
        />
      )
    },
    {
      field: 'agency',
      headerName: 'Agenzie',
      flex: 1.2,
      minWidth: 180,
      valueGetter: (value, row) => {
        if (row.role === 'siapAdmin') return 'SIAP'
        if (row.allAgencies?.length) return row.allAgencies.map((a) => a.name).join(', ')
        return row.agency?.name || agencyMap[row.agency?._id] || ''
      },
      renderCell: (params) => (
        <Typography variant='body2' fontWeight={500} noWrap title={params.value}>
          {params.value}
        </Typography>
      )
    },
    ...(canEdit
      ? [{
          field: 'actions',
          headerName: 'Azioni',
          width: 120,
          sortable: false,
          filterable: false,
          renderCell: (params) => (
            <Stack direction='row' spacing={0.5} alignItems='center'>
              <Tooltip title='Modifica utente'>
                <IconButton
                  size='small'
                  color='primary'
                  onClick={() => onEdit(params.row)}
                  aria-label={`Modifica ${params.row.name} ${params.row.lastName}`}
                >
                  <EditIcon fontSize='small' />
                </IconButton>
              </Tooltip>
              <Tooltip title={params.row.role === 'siapAdmin' ? 'Non puoi eliminare un Super Admin' : 'Elimina utente'}>
                <span>
                  <IconButton
                    size='small'
                    color='error'
                    onClick={() => onDelete(params.row)}
                    disabled={params.row.role === 'siapAdmin'}
                    aria-label={`Elimina ${params.row.name} ${params.row.lastName}`}
                  >
                    <DeleteIcon fontSize='small' />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          )
        }]
      : [])
  ]

  return (
    <>
      <Stack
        direction='row'
        alignItems='center'
        flexWrap='wrap'
        justifyContent='space-between'
        sx={{ gap: 1.5, mb: 1.5 }}
      >
        <Stack direction='row' alignItems='center' flexWrap='wrap' sx={{ gap: 1.5 }}>
          <FilterListIcon fontSize='small' color='action' />
          <Typography variant='body2' fontWeight={600} color='text.secondary'>
            Filtri
          </Typography>

          <TextField
            size='small'
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            placeholder='Cerca nome, cognome, email...'
            sx={{ width: 300 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon fontSize='small' color='action' />
                  </InputAdornment>
                )
              }
            }}
          />

          <FormControl size='small' sx={{ minWidth: 160 }}>
            <InputLabel id='role-filter-label'>Ruolo</InputLabel>
            <Select
              labelId='role-filter-label'
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              label='Ruolo'
            >
              <MenuItem value=''>
                <em>Tutti</em>
              </MenuItem>
              {Object.entries(ROLE_LABELS).map(([key, label]) => (
                <MenuItem key={key} value={key}>
                  <Chip label={label} color={ROLE_COLORS[key]} size='small' variant='outlined' sx={{ fontSize: 11 }} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {isAdmin && agencies?.length > 0 && (
            <FormControl size='small' sx={{ minWidth: 180 }}>
              <InputLabel id='agency-filter-label'>Agenzia</InputLabel>
              <Select
                labelId='agency-filter-label'
                value={agencyFilter}
                onChange={(e) => setAgencyFilter(e.target.value)}
                label='Agenzia'
              >
                <MenuItem value=''>
                  <em>Tutte</em>
                </MenuItem>
                {agencies.map((a) => (
                  <MenuItem key={a._id} value={a._id}>{a.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {hasActiveFilters && (
            <Chip
              label='Cancella filtri'
              size='small'
              variant='outlined'
              onDelete={handleClearFilters}
              onClick={handleClearFilters}
              sx={{ ml: 0.5 }}
            />
          )}
        </Stack>
      </Stack>

      <Box sx={{ width: '100%' }}>
        <DataGrid
          rows={filteredUsers}
          columns={columns}
          getRowId={(row) => row._id}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } }
          }}
          pageSizeOptions={[10, 25, 50]}
          disableRowSelectionOnClick
          disableColumnMenu
          localeText={{
            noRowsLabel: 'Nessun utente trovato.',
            MuiTablePagination: {
              labelRowsPerPage: 'Righe per pagina:',
              labelDisplayedRows: ({ from, to, count }) =>
                `${from}-${to} di ${count !== -1 ? count : `più di ${to}`}`
            }
          }}
          sx={dataGridSx}
        />
      </Box>
    </>
  )
}

const dataGridSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 2,
  '--DataGrid-containerBackground': '#004680',
  '& .MuiDataGrid-columnHeader': {
    color: '#fff',
    '&:focus, &:focus-within': { outline: 'none' }
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontWeight: 600,
    fontSize: 13,
    color: '#fff'
  },
  '& .MuiDataGrid-columnHeaderTitleContainer': {
    color: '#fff'
  },
  '& .MuiDataGrid-sortIcon': {
    color: '#fff'
  },
  '& .MuiDataGrid-iconButtonContainer .MuiIconButton-root': {
    color: '#fff'
  },
  '& .MuiDataGrid-cell': {
    borderBottom: '1px solid',
    borderBottomColor: 'divider',
    color: 'text.primary',
    display: 'flex',
    alignItems: 'center',
    '&:focus, &:focus-within': { outline: 'none' }
  },
  '& .MuiDataGrid-row': {
    '&:hover': {
      backgroundColor: 'action.hover'
    }
  },
  '& .MuiDataGrid-footerContainer': {
    borderTop: '1px solid',
    borderTopColor: 'divider',
    backgroundColor: 'transparent'
  },
  '& .MuiDataGrid-columnSeparator': {
    display: 'none'
  }
}
